import React from 'react';
import {
  Grid,
  GridItem,
  Header3Text,
  ISpruceFieldArrayProps,
  ISpruceFieldProps,
  NavigationItem,
  Select,
  SpaceWrapper,
  SmallBodyText,
  SpruceArrayField,
  SpruceField,
  TextInput,
  ISelectOption,
} from 'ui-toolkit';
import { v4 } from 'uuid';
import { IWhoHasFocus } from './IWhoHasFocus';
import { SignatoryForm } from './SignatoryForm';
import { ISignatorySchema } from '../validation/PartySchema';
import { optionsFromDisplay } from '../../../ui-toolkit/enums/optionsFromDisplay';
import { DisplayEntityType } from '../../../ui-toolkit/enums/eEntityType';
import { EditAddressDataEntry } from '../../../ui-toolkit/orders/components/EditPropertyAddress/EditAddressDataEntry';

interface IEntityFormProps {
  index: number;
  focus: IWhoHasFocus;
  setFocus: (x: IWhoHasFocus) => void;
}

export const EntityForm: React.FC<IEntityFormProps> = ({ index, focus, setFocus }) => {
  const optionsEntityType = optionsFromDisplay(DisplayEntityType);
  const filterOptions = (option: ISelectOption<string>, value: string) => {
    return option.value.toLowerCase().includes((value ?? '').toLowerCase());
  };
  const onChangeToState = (value: ISelectOption<string>) => value.value;
  const transformValueToSelect = (value: string) => optionsEntityType.find((t) => t.value === value);

  const fieldNameStart = `parties.${index}`;

  return (
    <Grid columnCount={3}>
      <SpruceField name={`${fieldNameStart}.entityName`}>
        {({ field }: ISpruceFieldProps) => (
          <TextInput
            {...field}
            data-test-id={`${fieldNameStart}.entity_name`}
            label={`Entity Name`}
            value={field.value}
            isRequired={true}
            placeholder={'ABC Real Estate Company'}
            autoFocus={focus.doesAnyone && focus.partyIndex === index && focus.signatoryIndex === -1}
          />
        )}
      </SpruceField>
      <SpruceField name={`${fieldNameStart}.entityType`}>
        {({ field, formik }: ISpruceFieldProps) => (
          <Select
            {...field}
            data-test-id={`${fieldNameStart}.entity_type`}
            value={transformValueToSelect(field.value)}
            options={optionsEntityType}
            filterOption={filterOptions}
            onChange={(value: any) => {
              formik.setFieldTouched(field.name);
              formik.setFieldValue(field.name, onChangeToState(value));
            }}
            label={`Entity Type`}
            isRequired={true}
            placeholder={`Select one...`}
          />
        )}
      </SpruceField>
      <GridItem columnStart={'span 12'}>
        <Header3Text>Current Address</Header3Text>
      </GridItem>
      <GridItem columnStart={`span 12`}>
        <EditAddressDataEntry name={`${fieldNameStart}.address`} isCamelCase />
      </GridItem>
      <GridItem columnStart={`span 12`}>
        <SpruceArrayField name={`${fieldNameStart}.signatories`}>
          {({ field }: ISpruceFieldArrayProps) => (
            <Grid columnCount={2}>
              {field.value.map((item: ISignatorySchema, indexOfSignatory: number) => (
                <GridItem columnStart={`span 6`}>
                  <SignatoryForm
                    indexOfParty={index}
                    indexOfSignatory={indexOfSignatory}
                    onClickDelete={() => field.remove(indexOfSignatory)}
                    autoFocus={
                      focus.doesAnyone && focus.partyIndex === index && focus.signatoryIndex === indexOfSignatory
                    }
                  />
                </GridItem>
              ))}
              <SpaceWrapper bottomSpace={'default'} topSpace={'default'} leftSpace={'none'} rightSpace={'none'}>
                <NavigationItem
                  onClick={() => {
                    field.push({ id: v4() });
                    setFocus({ doesAnyone: true, partyIndex: index, signatoryIndex: field.value.length - 1 });
                  }}
                  text={'+ Add Signatory'}
                  isInline={true}
                  data-test-id={`add_signatory_to_${index}`}
                >
                  <SmallBodyText>+ Add Signatory</SmallBodyText>
                </NavigationItem>
              </SpaceWrapper>
            </Grid>
          )}
        </SpruceArrayField>
      </GridItem>
    </Grid>
  );
};
